// Copyright 2025. WebPros International GmbH. All rights reserved.

import React from 'react';
import {
    Button,
    Toolbar,
    ToolbarExpander,
    ToolbarGroup,
    Translate,
} from '@plesk/ui-library';
import { DISASTER_RECOVERY_HISTORY } from 'admin/disasterRecovery/constants/test';
import { ActionButton } from 'common/components/PageHeader/Styles';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import ButtonWithConfirmation from 'common/components/ButtonWithConfirmation';
import { RootState } from 'admin/core/store';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import { connect } from 'react-redux';
import { COLORS } from 'admin/core/theme';
import {
    DisasterRecoveryEntityStatus as EntityResponseStatus,
    IDisasterRecoveryEntityResponse,
} from 'common/api/resources/DisasterRecovery';
import { COMPUTE_RESOURCE_STATUS } from 'admin/computeResource/constants';

interface IDRHistoryOperations {
    handleViewDeleteOriginalData: () => void;
    handleDeleteOriginalData: () => void;
    entities: IDisasterRecoveryEntityResponse[];
    processes: string[];
}

export type DisasterRecoveryHistoryOperationsProps =
    IDRHistoryOperations &
    ReturnType<typeof mapStateToProps>;

export const DisasterRecoveryHistoryOperations: React.FC<DisasterRecoveryHistoryOperationsProps> = ({
    handleViewDeleteOriginalData,
    isDeletingOriginalData,
    handleDeleteOriginalData,
    entities,
    processes,
}) => {
    let disabledDeleteButton = false;
    if (entities.length > 0) {
        disabledDeleteButton = entities.some(entity => entity.is_original_data_deleted
            || entity.disaster_recovery.source_compute_resource.status !== COMPUTE_RESOURCE_STATUS.ACTIVE
            || entity.is_migrated_back
            || entity.status !== EntityResponseStatus.COMPLETED);
    } else {
        disabledDeleteButton = processes.length === 0;
    }

    let tooltip = <Translate content="disasterRecovery.history.actions.deleteOriginalData.tooltip" />;
    if (entities.length > 0 && disabledDeleteButton) {
        tooltip = <Translate content="disasterRecovery.history.actions.deleteOriginalData.disabledTooltip" />;
    }

    return (
        <Toolbar>
            <ToolbarExpander/>
            <ToolbarGroup title="sync-image-presets-action">
                <ActionButton>
                    <ButtonWithConfirmation
                        data-cy={DISASTER_RECOVERY_HISTORY.DELETE_ORIGINAL_DATA_FOR_SELECTION}
                        handleConfirm={handleDeleteOriginalData}
                        disabled={disabledDeleteButton}
                        isLoading={isDeletingOriginalData}
                        confirmationButtonText={<Translate content="disasterRecovery.history.actions.deleteOriginalData.button"/>}
                        confirmationButtonGhost={false}
                        buttonIntent={INTENT_TYPE.PRIMARY}
                        buttonColor={COLORS.RED}
                        withStyledButton={true}
                        buttonSize={SIZE.LG}
                        translations={{
                            button: (
                                <Translate content="disasterRecovery.history.actions.deleteOriginalData.confirmationButton" />
                            ),
                            title: (
                                <Translate content="disasterRecovery.history.actions.deleteOriginalData.title" />
                            ),
                            tooltip,
                            text: (
                                <Translate content="disasterRecovery.history.actions.deleteOriginalData.text"/>
                            ),
                        }}
                    />
                </ActionButton>
                <ActionButton>
                    <Button
                        data-cy={DISASTER_RECOVERY_HISTORY.VIEW_ORIGINAL_DATA_FOR_SELECTION}
                        intent={INTENT_TYPE.PRIMARY}
                        ghost={false}
                        size={SIZE.LG}
                        onClick={handleViewDeleteOriginalData}
                        disabled={entities.length === 0 && processes.length === 0}
                        tooltip={<Translate content="disasterRecovery.history.actions.viewOriginalData.tooltip" />}
                    >
                        <Translate content="disasterRecovery.history.actions.viewOriginalData.button"/>
                    </Button>
                </ActionButton>
            </ToolbarGroup>
        </Toolbar>
    );
};

const mapStateToProps = (state: RootState) => ({
    isDeletingOriginalData: state.app.loadingFlags.has(LOADING_FLAGS.DISASTER_RECOVERY_ORIGINAL_DATA_DELETING),
});

export default connect(mapStateToProps)(DisasterRecoveryHistoryOperations);